(function () {
  const DEBOUNCE_MS = 150;

  function normalizeString(str) {
    return (str || '')
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .trim();
  }

  function tokenize(str) {
    return normalizeString(str)
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
  }

  /**
   * Match word variants dynamically (return / returns / returning)
   * by checking whether one token contains the other as a root.
   */
  function tokensLooselyMatch(a, b) {
    if (a === b) return true;

    const shorter = a.length <= b.length ? a : b;
    const longer = a.length > b.length ? a : b;

    // Ignore tiny tokens to avoid noisy matches (e.g. "to" → "tomorrow")
    if (shorter.length < 4) return false;

    return longer.includes(shorter);
  }

  function textMatchesQuery(entry, query) {
    if (!query) return true;
    if (entry.text.includes(query)) return true;

    const queryTokens = tokenize(query);
    if (!queryTokens.length) return false;

    return queryTokens.every((queryToken) =>
      entry.tokens.some((textToken) => tokensLooselyMatch(queryToken, textToken))
    );
  }

  function getHeaderOffset() {
    const stickyHeader = document.querySelector('sticky-header');
    if (stickyHeader) return stickyHeader.offsetHeight || 0;

    const sectionHeader = document.querySelector('.section-header');
    return sectionHeader ? sectionHeader.offsetHeight || 0 : 0;
  }

  function isMobileFaqNav() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  if (!customElements.get('faq-page')) {
    customElements.define(
      'faq-page',
      class FaqPage extends HTMLElement {
        constructor() {
          super();
          this.searchTimeout = null;
          this.isSearchActive = false;
          this.spyObserver = null;
          this.stickyObserver = null;
          this._onScrollSpy = null;
          this._spyRaf = null;
          this._ignoreSpyUntil = 0;
        }

        connectedCallback() {
          this.nav = this.querySelector('[data-faq-nav]');
          this.navLinks = Array.from(this.querySelectorAll('[data-faq-nav-link]'));
          this.categories = Array.from(this.querySelectorAll('[data-faq-category]'));
          this.searchInput = this.querySelector('[data-faq-search]');
          this.searchClear = this.querySelector('[data-faq-search-clear]');
          this.searchResults = this.querySelector('[data-faq-search-results]');
          this.searchResultsList = this.querySelector('[data-faq-search-results-list]');
          this.searchHeading = this.querySelector('[data-faq-search-heading]');
          this.searchListCta = this.searchResultsList?.querySelector('[data-faq-list-cta]');
          this.categoriesWrap = this.querySelector('[data-faq-categories]');
          this.emptyState = this.querySelector('[data-faq-empty]');
          this.stickyToolbar = this.querySelector('[data-faq-sticky-toolbar]');
          this.stickySentinel = this.querySelector('[data-faq-sticky-sentinel]');
          this.items = Array.from(this.querySelectorAll('[data-faq-item]'));

          this._onResize = this.onResize.bind(this);

          this.buildSearchIndex();
          this.bindNav();
          this.bindSearch();
          this.setupStickyToolbar();
          this.setupScrollSpy();

          const hash = window.location.hash ? window.location.hash.slice(1) : '';
          if (hash && this.querySelector(`#${hash}`)) {
            this.handleInitialHash();
          } else if (this.navLinks[0]) {
            this.setActiveNav(this.navLinks[0].dataset.categoryId, { force: true });
          }

          window.addEventListener('resize', this._onResize, { passive: true });
        }

        disconnectedCallback() {
          if (this.spyObserver) this.spyObserver.disconnect();
          if (this.stickyObserver) this.stickyObserver.disconnect();
          if (this._onScrollSpy) {
            window.removeEventListener('scroll', this._onScrollSpy);
            this._onScrollSpy = null;
          }
          if (this._spyRaf) cancelAnimationFrame(this._spyRaf);
          if (this.searchTimeout) clearTimeout(this.searchTimeout);
          window.removeEventListener('resize', this._onResize);
        }

        onResize() {
          this.updateStickyOffset();
          this.setupStickyToolbar();
          this.setupScrollSpy();
        }

        updateStickyOffset() {
          const headerOffset = getHeaderOffset();
          this.style.setProperty('--faq-page-sticky-top', `${headerOffset}px`);
        }

        handleInitialHash() {
          const hash = window.location.hash ? window.location.hash.slice(1) : '';
          if (!hash || !this.querySelector(`#${hash}`)) return;

          requestAnimationFrame(() => {
            this.scrollToCategory(hash);
            this.setActiveNav(hash, { force: true });
          });
        }

        buildSearchIndex() {
          this.searchIndex = this.items.map((item) => {
            const text = normalizeString(item.dataset.searchText);
            return {
              item,
              list: item.parentElement,
              text,
              tokens: tokenize(text),
            };
          });
        }

        formatSearchHeading(count, rawQuery) {
          return `${count} SEARCH RESULTS FOR "${rawQuery.toUpperCase()}"`;
        }

        restoreAllItems() {
          this.searchIndex.forEach(({ item, list }) => {
            if (!list) return;
            const cta = list.querySelector('[data-faq-list-cta]');
            if (cta) {
              list.insertBefore(item, cta);
            } else {
              list.appendChild(item);
            }
          });
        }

        closeAllAccordions() {
          this.querySelectorAll('.faqs__details').forEach((details) => {
            details.classList.remove('faq-accordion--active', 'faq-accordion--closing');
            details.open = false;
            const summary = details.querySelector('summary');
            summary?.setAttribute('aria-expanded', 'false');
          });
        }

        bindNav() {
          this.navLinks.forEach((link) => {
            link.addEventListener('click', (event) => {
              event.preventDefault();
              const categoryId = link.dataset.categoryId;
              if (!categoryId) return;

              if (this.isSearchActive) {
                this.clearSearch({ focusSearch: false });
              }

              // Keep clicked pill active while smooth-scroll finishes
              this._ignoreSpyUntil = performance.now() + 900;
              this.setActiveNav(categoryId, { force: true });
              this.scrollToCategory(categoryId);
            });
          });
        }

        bindSearch() {
          if (!this.searchInput) return;

          this.searchInput.addEventListener('input', () => {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => this.runSearch(), DEBOUNCE_MS);
          });

          if (this.searchClear) {
            this.searchClear.addEventListener('click', () => {
              this.clearSearch({ focusSearch: true });
            });
          }
        }

        runSearch() {
          const rawQuery = this.searchInput.value.trim();
          const query = normalizeString(rawQuery);
          this.isSearchActive = query.length > 0;
          this.classList.toggle('faq-page--search-active', this.isSearchActive);

          if (!this.isSearchActive) {
            this.clearSearch({ focusSearch: false });
            return;
          }

          this.clearActiveNav();

          if (this.searchResultsList && this.categoriesWrap) {
            this.restoreAllItems();
            this.closeAllAccordions();

            const matches = this.searchIndex.filter((entry) => textMatchesQuery(entry, query));

            matches.forEach(({ item }) => {
              if (this.searchListCta) {
                this.searchResultsList.insertBefore(item, this.searchListCta);
              } else {
                this.searchResultsList.appendChild(item);
              }
            });

            this.categoriesWrap.hidden = true;
            this.searchResults.hidden = false;

            if (this.searchHeading) {
              this.searchHeading.textContent = this.formatSearchHeading(matches.length, rawQuery);
              this.searchHeading.hidden = false;
            }

            if (this.emptyState) {
              this.emptyState.hidden = true;
            }
          } else {
            let visibleCount = 0;

            this.searchIndex.forEach((entry) => {
              const match = textMatchesQuery(entry, query);
              entry.item.classList.toggle('faq-page__item--hidden', !match);
              if (match) visibleCount += 1;
            });

            this.categories.forEach((category) => {
              const visibleItems = category.querySelectorAll('[data-faq-item]:not(.faq-page__item--hidden)');
              category.classList.toggle('faq-page__category--hidden', visibleItems.length === 0);
            });

            if (this.emptyState) {
              this.emptyState.hidden = visibleCount > 0;
            }
          }

          if (this.searchClear) {
            this.searchClear.classList.toggle('faq-page__search-clear--visible', rawQuery.length > 0);
          }
        }

        clearSearch({ focusSearch }) {
          if (!this.searchInput) return;

          this.searchInput.value = '';
          this.isSearchActive = false;
          this.classList.remove('faq-page--search-active');

          if (this.searchResultsList && this.categoriesWrap) {
            this.restoreAllItems();

            this.searchResults.hidden = true;
            this.categoriesWrap.hidden = false;

            if (this.searchHeading) {
              this.searchHeading.textContent = '';
              this.searchHeading.hidden = true;
            }
          } else {
            this.items.forEach((item) => item.classList.remove('faq-page__item--hidden'));
            this.categories.forEach((category) => category.classList.remove('faq-page__category--hidden'));
          }

          if (this.emptyState) this.emptyState.hidden = true;
          if (this.searchClear) this.searchClear.classList.remove('faq-page__search-clear--visible');

          if (focusSearch) this.searchInput.focus();

          const activeFromSpy = this.getActiveCategoryFromScroll();
          if (activeFromSpy) {
            this.setActiveNav(activeFromSpy, { force: true });
          } else if (this.navLinks[0]) {
            this.setActiveNav(this.navLinks[0].dataset.categoryId, { force: true });
          }
        }

        setActiveNav(categoryId, { force } = {}) {
          if (!categoryId) return;
          if (this.isSearchActive && !force) return;

          const alreadyActive =
            this.navLinks.find((link) => link.classList.contains('is-active'))?.dataset.categoryId ===
            categoryId;

          let activeLink = null;

          this.navLinks.forEach((link) => {
            const isActive = link.dataset.categoryId === categoryId;
            link.classList.toggle('is-active', isActive);
            if (isActive) {
              link.setAttribute('aria-current', 'true');
              activeLink = link;
            } else {
              link.removeAttribute('aria-current');
            }
          });

          // Horizontal pill scroll only — never use scrollIntoView (it fights page scroll on iOS).
          if (activeLink && !alreadyActive && isMobileFaqNav()) {
            this.scrollPillIntoView(activeLink);
          }
        }

        scrollPillIntoView(activeLink) {
          const list = this.querySelector('.faq-page__nav-list');
          if (!list || !activeLink || !isMobileFaqNav()) return;

          const listRect = list.getBoundingClientRect();
          const linkRect = activeLink.getBoundingClientRect();
          const pad = 12;

          if (linkRect.left < listRect.left + pad) {
            list.scrollLeft += linkRect.left - listRect.left - pad;
          } else if (linkRect.right > listRect.right - pad) {
            list.scrollLeft += linkRect.right - listRect.right + pad;
          }
        }

        clearActiveNav() {
          this.navLinks.forEach((link) => {
            link.classList.remove('is-active');
            link.removeAttribute('aria-current');
          });
        }

        getScrollOffset() {
          const headerOffset = getHeaderOffset();
          const toolbarHeight = this.stickyToolbar ? this.stickyToolbar.offsetHeight : 0;
          return headerOffset + toolbarHeight + 16;
        }

        scrollToCategory(categoryId) {
          const target = this.querySelector(`#${categoryId}`);
          if (!target) return;

          const top = target.getBoundingClientRect().top + window.scrollY - this.getScrollOffset();
          window.scrollTo({ top, behavior: 'smooth' });
        }

        getActiveCategoryFromScroll() {
          const offset = this.getScrollOffset();
          let activeId = null;

          // Last category whose top has crossed under the sticky nav wins (DOM order).
          this.categories.forEach((category) => {
            if (category.classList.contains('faq-page__category--hidden')) return;
            const top = category.getBoundingClientRect().top - offset;
            if (top <= 12) {
              activeId = category.id;
            }
          });

          if (!activeId) {
            const firstVisible = this.categories.find(
              (c) => !c.classList.contains('faq-page__category--hidden')
            );
            activeId = firstVisible ? firstVisible.id : null;
          }

          return activeId;
        }

        syncActiveNavFromScroll() {
          if (this.isSearchActive) return;
          if (performance.now() < this._ignoreSpyUntil) return;

          const activeId = this.getActiveCategoryFromScroll();
          if (activeId) this.setActiveNav(activeId);
        }

        setupScrollSpy() {
          if (!this.categories.length) return;

          if (this.spyObserver) {
            this.spyObserver.disconnect();
            this.spyObserver = null;
          }

          if (this._onScrollSpy) {
            window.removeEventListener('scroll', this._onScrollSpy);
            this._onScrollSpy = null;
          }

          if (this._spyRaf) {
            cancelAnimationFrame(this._spyRaf);
            this._spyRaf = null;
          }

          this._onScrollSpy = () => {
            if (this._spyRaf) return;
            this._spyRaf = requestAnimationFrame(() => {
              this._spyRaf = null;
              this.syncActiveNavFromScroll();
            });
          };

          window.addEventListener('scroll', this._onScrollSpy, { passive: true });
          this.syncActiveNavFromScroll();
        }

        setupStickyToolbar() {
          if (!this.stickyToolbar || !this.stickySentinel || typeof IntersectionObserver === 'undefined') return;

          if (this.stickyObserver) this.stickyObserver.disconnect();

          this.updateStickyOffset();

          // Desktop uses sidebar sticky; mobile pins the pill toolbar under the header.
          if (!isMobileFaqNav()) {
            this.stickyToolbar.classList.remove('faq-page__toolbar--pinned');
            return;
          }

          this.stickyObserver = new IntersectionObserver(
            ([entry]) => {
              this.stickyToolbar.classList.toggle('faq-page__toolbar--pinned', !entry.isIntersecting);
              this.updateStickyOffset();
            },
            {
              root: null,
              threshold: 0,
              rootMargin: `-${getHeaderOffset()}px 0px 0px 0px`,
            }
          );

          this.stickyObserver.observe(this.stickySentinel);
        }
      }
    );
  }
})();
